import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, interval, Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { UserAttributesInterface } from '../Auth/bs-auth.service';
import { BsApiService } from '../API/bs-api.service';
import { SimpleGlobalModalService } from '../GlobalModal/simple-global-modal.service';
import { Environment } from '@micro-core/environment';

export interface FeatureFlags {
  /** Flag values from API may be string ('true'/'false') or boolean. */
  flags: { [key: string]: string | boolean | null };
  expirationTS: string;
}

export interface FeatureFlagPayload {
  payload: FeatureFlags;
}

/** One flag row for dev menu: key, value from LaunchDarkly, and optional dev override. */
export interface FeatureFlagDisplayRow {
  key: string;
  ldValue: boolean | null;
  override: boolean | null;
  effective: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private _bsApi: BsApiService = inject(BsApiService);
  private _globalModalSvc: SimpleGlobalModalService = inject(SimpleGlobalModalService);
  private _env: Environment = inject(Environment);
  private _featureFlags = new BehaviorSubject<FeatureFlags | null>(null);
  private _featureFlagsReady = new BehaviorSubject<boolean>(false);
  public readonly featureFlagsReady: Observable<boolean> = this._featureFlagsReady.asObservable();
  private readonly CACHE_KEY = 'featureFlags';
  private readonly CHECK_INTERVAL = 10000; // 10 seconds
  private _isInitialized: boolean;

  /** Dev-only overrides: when set, getFlag() returns this instead of LD value. */
  private _devOverrides: Record<string, boolean> = {};
  private _devOverrides$ = new BehaviorSubject<Record<string, boolean>>({});

  constructor() {}

  disableClient(): void {
    this._featureFlagsReady.next(false);
    this._featureFlags.next(null);
    localStorage.removeItem(this.CACHE_KEY);
  }

  initializeFeatureFlag(user: UserAttributesInterface): void {
    if (!user.guid || this._isInitialized) {
      return;
    }
    this._isInitialized = true;
    this._fetchFeatureFlags()
      .pipe(
        tap(() => this._featureFlagsReady.next(true)),
        switchMap(() =>
          interval(this.CHECK_INTERVAL).pipe(
            distinctUntilChanged(),
            filter(() => this._shouldFetchFlags()),
            switchMap(() => this._fetchFeatureFlags())
          )
        )
      )
      .subscribe();
  }

  private _fetchFeatureFlags(): Observable<FeatureFlags | null> {
    return this._bsApi.get<FeatureFlagPayload>('api-mobile', '/flag').pipe(
      map((response) => response.payload),
      tap((flags) => {
        this._featureFlags.next(flags);
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(flags));
      }),
      catchError((error): Observable<FeatureFlags | null> => {
        console.error('Feature flags fetch failed', error);
        const cachedFlags = this._getCachedFlags();
        if (cachedFlags) {
          this._featureFlags.next(cachedFlags);
          return of(cachedFlags);
        } else {
          this._globalModalSvc.createGenericErrorModal('feature_flag_error', () => {
            this._fetchFeatureFlags();
            this._globalModalSvc.clearModal();
          });
          return of(null);
        }
      })
    );
  }

  private _shouldFetchFlags(): boolean {
    const flags = this._featureFlags.getValue();
    if (!flags) return true;
    const expirationTS = Date.parse(flags.expirationTS);
    return Date.now() >= expirationTS;
  }

  private _getCachedFlags(): FeatureFlags | null {
    const cachedData = localStorage.getItem(this.CACHE_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  }

  /** Normalize API flag value (string or boolean) to boolean. */
  private _flagValueToBoolean(val: string | boolean | null | undefined): boolean {
    return val === true || val === 'true';
  }

  /** Normalize API flag value to true | false | null for display. */
  private _flagValueToTriState(val: string | boolean | null | undefined): boolean | null {
    if (val === true || val === 'true') return true;
    if (val === false || val === 'false') return false;
    return null;
  }

  getFlag<T>(flag: string, defaultVal?: T): Observable<T> {
    return this.featureFlagsReady.pipe(
      filter((ready) => ready),
      take(1),
      map(() => {
        if (!this._env.production && this._devOverrides.hasOwnProperty(flag)) {
          return this._devOverrides[flag] as T;
        }
        const flags = this._featureFlags.getValue();
        return (flags?.flags[flag] ?? defaultVal) as T;
      })
    );
  }

  /** Current flags snapshot from LaunchDarkly (for dev menu). */
  getFlagsSnapshot(): { [key: string]: string | boolean | null } | null {
    return this._featureFlags.getValue()?.flags ?? null;
  }

  /** Set a dev-only override for a flag. Pass null to clear. */
  setDevOverride(flag: string, value: boolean | null): void {
    if (this._env.production) return;
    if (value === null) {
      delete this._devOverrides[flag];
    } else {
      this._devOverrides[flag] = value;
    }
    this._devOverrides$.next({ ...this._devOverrides });
  }

  /** Get current dev override for a flag, or null if none. */
  getDevOverride(flag: string): boolean | null {
    return this._devOverrides.hasOwnProperty(flag) ? this._devOverrides[flag] : null;
  }

  /**
   * Observable of flag rows for dev menu: keys from flagKeys, with LD value and override.
   * effective = override if set, else LD value (truthy).
   */
  getFlagsForDisplay(flagKeys: string[]): Observable<FeatureFlagDisplayRow[]> {
    return combineLatest([
      this._featureFlagsReady.pipe(filter((r) => r)),
      this._devOverrides$,
    ]).pipe(
      take(1),
      map(() => {
        const flags = this._featureFlags.getValue()?.flags ?? {};
        return flagKeys.map((key) => {
          const ldRaw = flags[key];
          const ldValue = this._flagValueToTriState(ldRaw);
          const override = this.getDevOverride(key);
          const effective = override !== null ? override : ldValue === true;
          return { key, ldValue, override, effective };
        });
      })
    );
  }

  /** Emit latest flags for display when overrides or LD flags change (for dev menu live updates). */
  getFlagsForDisplayLive(flagKeys: string[]): Observable<FeatureFlagDisplayRow[]> {
    return combineLatest([
      this._featureFlags.pipe(filter((ff): ff is FeatureFlags => ff != null)),
      this._devOverrides$,
    ]).pipe(
      map(([ff]) => {
        const flags = ff.flags ?? {};
        return flagKeys.map((key) => {
          const ldRaw = flags[key];
          const ldValue = this._flagValueToTriState(ldRaw);
          const override = this.getDevOverride(key);
          const effective = override !== null ? override : ldValue === true;
          return { key, ldValue, override, effective };
        });
      }),
      startWith([] as FeatureFlagDisplayRow[])
    );
  }

  checkMultipleFlags(flags: string[]): Observable<boolean> {
    return this.featureFlagsReady.pipe(
      filter((ready) => ready),
      take(1),
      map(() => {
        const flagValues = flags.map((flag) => {
          if (!this._env.production && this._devOverrides.hasOwnProperty(flag)) {
            return this._devOverrides[flag];
          }
          const rawVal = this._featureFlags.getValue()?.flags[flag];
          return this._flagValueToBoolean(rawVal);
        });
        return !flagValues.includes(false);
      })
    );
  }
}
